import { Box, Typography, SxProps, Theme } from "@mui/material";
import { SectionContainer } from "../../../modules/SectionContainer/SectionContainer";
import { SectionTitle } from "../../../modules/SectionTitle/SectionTitle";
import { SectionSubtitle } from "../../../modules/SectionSubtitle/SectionSubtitle";

const industries = [
  "自動車メーカー",
  "食品メーカー",
  "フードデリバリー",
  "メディア",
  "SaaS",
  "製造業",
  "情報サービス",
  "電機メーカー",
  "ITコンサル",
  "コンビニ大手",
  "航空会社",
  "介護・医療機器",
];

const cases = [
  {
    client: "介護支援機器メーカー",
    title: "施設向け管理システムの新規開発・運用支援",
    challenge:
      "新しい排泄検知デバイスの管理システムをゼロから構築。スマホを含むマルチデバイス対応と、全国展開を見据えたインフラ基盤が必要だった",
    support: "要件定義から開発・継続運用まで一貫支援。画面仕様作成・機能改修・大規模改修にも柔軟対応",
    result: "全国約50施設・約500デバイスが常時接続する安定したシステムを実現",
    tech: "TypeScript / React / Flutter / AWS（ECS, Lambda, RDS, CloudFront, WAF）",
  },
  {
    client: "コンビニ大手",
    title: "新デリバリー事業における開発支援",
    challenge:
      "最短15分配達の即時デリバリーサービス向けに、在庫・販売状況をリアルタイム連携する基盤システムの開発が必要だった",
    support:
      "アーキテクトレベルからインプリメントレベルまで垂直統合的に支援。エース級のフルスタックメンバーを人選し対応",
    result: "予定納期より早く納品完了。テクニカルQAの即時対応で開発スピードを大幅改善",
    tech: "Java / TypeScript / SpringBoot / node.js / AWS / RDS",
  },
  {
    client: "航空会社",
    title: "予約・搭乗状況管理のクラウド化および他社連携ツールの開発支援",
    challenge: "国際線・国内線の予約・搭乗管理をクラウドへ移行し、他社との連携まで可能なシステムが必要だった",
    support: "共通ライブラリ開発・帳票類開発・システムテスト統括。PMOとしてエンドユーザー担当者のサポートも実施",
    result: "搭乗証明書・eチケット・お客様控え情報の管理帳票などを開発し、クラウド移行を完遂",
    tech: "JAVA / Springboot / React / TypeScript / AWS / Docker",
  },
];

export const AchievementsSection = () => {
  return (
    <SectionContainer sx={{ background: "#f8f9fa", padding: { md: "100px 0", xs: "60px 0" } }}>
      <SectionTitle sx={{ fontSize: { md: "40px", xs: "26px" } }}>信頼は、実績で語る。</SectionTitle>
      <SectionSubtitle sx={{ maxWidth: "700px" }}>
        2022年の創業以来、自動車・航空・コンビニ大手など多様な業界の企業様の開発を支援してきました。規模を問わず、質の高い開発で信頼を積み重ねています。
      </SectionSubtitle>

      <Box sx={industriesContainerSx}>
        {industries.map((industry, index) => (
          <Box key={index} sx={industryTagSx}>
            {industry}
          </Box>
        ))}
      </Box>

      <Box sx={casesContainerSx}>
        {cases.map((caseItem, index) => (
          <Box key={index} sx={caseCardSx}>
            <Typography sx={clientSx}>{caseItem.client}</Typography>
            <Typography sx={caseTitleSx}>{caseItem.title}</Typography>

            <Box sx={caseDetailSx}>
              <Typography sx={labelSx}>課題</Typography>
              <Typography sx={valueSx}>{caseItem.challenge}</Typography>
            </Box>

            <Box sx={caseDetailSx}>
              <Typography sx={labelSx}>支援内容</Typography>
              <Typography sx={valueSx}>{caseItem.support}</Typography>
            </Box>

            <Box sx={caseDetailSx}>
              <Typography sx={labelSx}>成果</Typography>
              <Typography sx={valueSx}>{caseItem.result}</Typography>
            </Box>

            <Box sx={techContainerSx}>
              <Typography sx={techLabelSx}>技術</Typography>
              <Typography sx={techValueSx}>{caseItem.tech}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </SectionContainer>
  );
};

const industriesContainerSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  justifyContent: "center",
  marginTop: { md: "40px", xs: "28px" },
  marginBottom: { md: "48px", xs: "32px" },
};

const industryTagSx: SxProps<Theme> = {
  background: "#FFFFFF",
  border: "1px solid #E0E0E0",
  borderRadius: "20px",
  padding: "8px 16px",
  fontSize: { md: "14px", xs: "12px" },
  fontWeight: 500,
  color: "#333333",
};

const casesContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: { md: "24px", xs: "16px" },
};

const caseCardSx: SxProps<Theme> = {
  background: "#FFFFFF",
  borderRadius: "12px",
  padding: { md: "32px", xs: "20px" },
  borderLeft: "4px solid #22A1B9",
};

const clientSx: SxProps<Theme> = {
  fontSize: { md: "13px", xs: "12px" },
  fontWeight: 600,
  color: "#22A1B9",
  marginBottom: "8px",
};

const caseTitleSx: SxProps<Theme> = {
  fontSize: { md: "20px", xs: "17px" },
  fontWeight: 700,
  color: "#222222",
  marginBottom: { md: "24px", xs: "20px" },
};

const caseDetailSx: SxProps<Theme> = {
  marginBottom: "16px",
};

const labelSx: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#999999",
  marginBottom: "4px",
};

const valueSx: SxProps<Theme> = {
  fontSize: { md: "14px", xs: "13px" },
  color: "#333333",
  lineHeight: 1.7,
};

const techContainerSx: SxProps<Theme> = {
  marginTop: "20px",
  paddingTop: "16px",
  borderTop: "1px solid #E0E0E0",
};

const techLabelSx: SxProps<Theme> = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#22A1B9",
  marginBottom: "4px",
};

const techValueSx: SxProps<Theme> = {
  fontSize: { md: "13px", xs: "12px" },
  color: "#666666",
  fontFamily: "monospace",
};
